/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment';

// Component imports
import { Avatar, Button, Col, Menu, Popover, Row, Space, Typography } from 'antd';
import { GlobalOutlined, UserOutlined, CopyOutlined, EllipsisOutlined, DownloadOutlined, LinkOutlined } from '@ant-design/icons';
import ConversationStatusTag from './ConversationStatusTag';
import { AgentAcceptIcon } from '../Icons/AgentAcceptIcon';
import { ImportantFlag } from '../Icons/liveChatFlags/ImportantFlag';
import { ImportantIcon } from '../Icons/ImportantIcon';

// Misc imports
import formatEndUserName from '../../utils/string/formatEndUserName';
import formatTimestamp from '../../utils/misc/formatTimestamp';
import generateEnvironmentUrl from '../../utils/misc/generateEnvironmentUrl';
import handleFileDownload from '../../utils/misc/handleFileDownload';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { CONVERSATION_STATUSES, LIVECHAT_MODES, LIVECHAT_SCREEN_TABS } from '../../constants/liveChat';
import { NAV_LIVECHAT } from '../../constants/navigation';
import { PLATFORMS } from '../../constants/main';
import { copyTextToClipboard } from '../../utils/function/copyToClipboard';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { removeMarkdown } from '../../utils/string/removeMarkdown';
import { revertBlobTypes } from '../../store/utils/revertBlobTypes';
import { useLazyExportConversationTranscriptCsvQuery } from '../../store/liveChat/liveChatApi';
import useTheme from '../../utils/hooks/useTheme';

// Constant declarations
const { Text } = Typography;

const InboxItem = ({
  conversationId,
  sessionId,
  endUserName,
  tenantImageUrl,
  lastMessage,
  startDate,
  status,
  platform,
  livechatParticipants,
  transferRequests,
  isImportant,
  onClick,
  onImportantClick
}) => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { borderColorBase } = useTheme();

  // Redux state
  const { currentUser } = useSelector((state) => state.auth);
  const { currentData: activeConversationInfo } = getCurrentQueryState('fetchActiveConversation', { orgId, conversationId: searchParams.get('conversation') });
  const { data: livechatSettings } = getCurrentQueryState('fetchLiveChatApplicationSettings', { orgId });
  const [exportConversationTranscript, { isLoading: isExportingTranscript }] = useLazyExportConversationTranscriptCsvQuery();
  const { data: organization } = getCurrentQueryState('fetchOrganization', { orgId });
  const orgTimezone = organization?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Local state
  const [statusTag, setStatusTag] = useState();
  const [isUnread, setIsUnread] = useState(false);
  const [isParticipant, setIsParticipant] = useState(false);
  const [acceptedBy, setAcceptedBy] = useState('');

  useEffect(() => {
    const agentData = livechatParticipants?.find(participant => participant.platformUserId === currentUser.id);

    const agentHasUnread = agentData?.lastMessageRead === false;
    setIsUnread(agentHasUnread && conversationId !== activeConversationInfo?.id);

    setIsParticipant(!!agentData);
  }, [livechatParticipants]);

  useEffect(() => {
    if (transferRequests && transferRequests.length) {
      const agent = transferRequests.reduce((prev, curr) => {
        return moment(prev.requestedDate).isAfter(curr.requestedDate) ? prev : curr;
      })?.acceptedBy;
      // don't update until next agent accepts the request
      if (agent) {
        setAcceptedBy(agent);
      }
    }
  }, [transferRequests]);

  useEffect(() => {
    if (status === CONVERSATION_STATUSES.FINISHED) {
      return setStatusTag('Finished');
    }
    if (livechatParticipants?.length && livechatSettings?.livechatMode === LIVECHAT_MODES.SEMI_PRIVATE) {
      const me = livechatParticipants.find(agent => agent.platformUserId === currentUser.id && agent.status === 'transferred');
      if (me) {
        return setStatusTag('Transferred');
      }
    }
    if (livechatParticipants?.length && transferRequests?.length) {
      const latestTransfer = transferRequests.reduce((prev, curr) => {
        return moment(prev.requestedDate).isAfter(curr.requestedDate) ? prev : curr;
      });
      const me = livechatParticipants.find(agent => agent.platformUserId === currentUser.id && agent.status === 'active' && agent.isOwner);
      if (latestTransfer?.status === 'new' && latestTransfer.type === 'transfer' && me) {
        return setStatusTag('Pending');
      }
    }
    return setStatusTag('Active');
  }, [livechatParticipants, transferRequests, livechatSettings?.livechatMode]);

  const formatLastMessageTitle = (text) => {
    let lastMessage = removeMarkdown(text ?? t('This is the start of the conversation'));
    if (lastMessage.length > 99) {
      lastMessage = lastMessage.substring(0, 99) + '...';
    }
    return lastMessage;
  };

  const onTranscriptExport = async () => {
    const result = await exportConversationTranscript({ orgId, urlParams: {
      conversation: conversationId,
      order: 'ASC'
    } }).unwrap();

    const file = revertBlobTypes(result, 'application/json');

    if (file) {
      const fileName = file.headers['content-disposition']?.split(new RegExp(['"'], 'g'))?.[1] ?? `transcript.csv`;
      handleFileDownload(file.blob, fileName);
    }
  };

  const renderConversationStatus = () => {
    const tags = [];

    switch (statusTag) {
      case 'Active':
        tags.push(<ConversationStatusTag type="active" />);
        break;
      case 'Finished':
        tags.push(<ConversationStatusTag type="finished" />);
        break;
      case 'Transferred':
        tags.push(<ConversationStatusTag type="transferred" />);
        break;
      case 'Pending':
        tags.push(<ConversationStatusTag type="new" />);
        tags.push(<ConversationStatusTag type="transfer" />);
        break;
      default:
        break;
    }

    if (livechatSettings?.livechatMode === LIVECHAT_MODES.PUBLIC && livechatParticipants.find(participant => participant.platformUserId === currentUser.id)) {
      tags.push(<ConversationStatusTag type="joined" />);
    }

    return tags;
  };

  const MemoizedInboxItem = useMemo(() => (
    <Row
      className={classNames('inbox-item', {
        'selected-inbox-item': Number(searchParams.get('conversation')) === conversationId
      })}
      align="middle"
      wrap={false}
      onClick={onClick}
    >
      {
        isImportant ? (
          <Col className="pr-2">
            <ImportantFlag />
          </Col>
        ) : null
      }

      <Col
        className={classNames({ 'ml-5': !isImportant })}
        flex="72px"
      >
        <Avatar.Group className="v-align-middle">
          <Avatar
            size={52}
            className="fs-xl text-color-default fw-300"
            style={{ backgroundColor: borderColorBase }}
          >
            {endUserName.trim() && endUserName ? formatEndUserName(endUserName) : <UserOutlined/>}
          </Avatar>

          <Avatar
            src={tenantImageUrl}
            className="mt-auto"
            style={{ borderWidth: 3 }}
            size={28}
          />
        </Avatar.Group>
      </Col>

      <Col
        flex="auto"
        className="pl-2"
      >
        <Row
          wrap={false}
          align="middle"
          gutter={8}
          style={{ lineHeight: 1.2 }}
        >
          <Col flex="none"
            style={{ maxWidth: '70%' }}
          >
            <Typography.Text
              title={endUserName || t('Unknown user')}
              className="fw-500"
              ellipsis
            >
              {endUserName || t('Unknown user')}
            </Typography.Text>
          </Col>

          <Col flex="none" className="text-muted">
            {Object.values(PLATFORMS).find(platformType => platformType.key === platform)?.icon ?? <GlobalOutlined />}
          </Col>

          <Col className="text-right" flex="auto">
            <Popover
              placement="rightTop"
              title={<Text type="secondary">{t('Conversation Quick Actions')}</Text>}
              content={
                <Menu
                  className="d-contents"
                  items={
                    [
                      ...isParticipant ? [{
                        key: 'mark-important',
                        icon: <ImportantIcon />,
                        label: !isImportant ? t('Mark as Important') : t('Mark as not Important'),
                        onClick: (e) => {
                          e.domEvent.stopPropagation();
                          onImportantClick(isImportant);
                        }
                      }] : [],
                      {
                        key: 'copy-session-id',
                        icon: <CopyOutlined />,
                        label: t('Copy Session ID'),
                        onClick: (e) => {
                          e.domEvent.stopPropagation();
                          copyTextToClipboard(sessionId, t('Session ID copied!'));
                        }
                      },
                      {
                        key: 'copy-conversation-id',
                        icon: <CopyOutlined />,
                        label: t('Copy Conversation ID'),
                        onClick: (e) => {
                          e.domEvent.stopPropagation();
                          copyTextToClipboard(conversationId, t('Conversation ID copied!'));
                        }
                      },
                      {
                        key: 'download-transcript',
                        icon: <DownloadOutlined />,
                        label: t('Download Transcript'),
                        onClick: (e) => {
                          e.domEvent.stopPropagation();
                          onTranscriptExport();
                        },
                        loading: isExportingTranscript
                      },
                      {
                        key: 'copy-conversation-link',
                        icon: <LinkOutlined />,
                        label: t('Copy Link'),
                        onClick: (e) => {
                          e.domEvent.stopPropagation();
                          copyTextToClipboard(replaceUrlParams(`${generateEnvironmentUrl() + NAV_LIVECHAT}/?tab=${LIVECHAT_SCREEN_TABS.OPEN}&conversation=:conversationId`, { orgId: orgId, conversationId: conversationId }), 'Link copied to clipboard');
                        }
                      }
                    ]
                  }
                />
              }
              trigger="click"
              showArrow={false}
              overlayClassName="p-0 pl-3"
            >
              <Button type="text" onClick={(e) => e.stopPropagation()} icon={<EllipsisOutlined />}/>
            </Popover>
          </Col>
        </Row>

        <Row
          wrap={false}
          align="middle"
          gutter={8}
        >
          <Col flex="auto">
            <Typography.Paragraph
              className="fs-sm mb-0"
              title={acceptedBy}
              ellipsis
            >
              <AgentAcceptIcon className="mr-1" />
              {acceptedBy}
            </Typography.Paragraph>

            <Typography.Paragraph
              className={classNames('fs-sm mb-0', {
                'text-color-default': isUnread,
                'text-muted': !isUnread
              })}
              strong={isUnread}
              title={formatLastMessageTitle(lastMessage?.text)}
              ellipsis
            >
              {isUnread ? <div className="unread-item-notification"></div> : null}

              {removeMarkdown(lastMessage?.text ?? t('This is the start of the conversation'))}
            </Typography.Paragraph>
          </Col>

          <Col>
            <Space>
              <Space.Compact direction="vertical" className="align-items-end">
                <Typography.Text
                  className="text-secondary float-right pr-2 fs-sm"
                >
                  {formatTimestamp(lastMessage?.sentDate ?? startDate, false, orgTimezone)}
                </Typography.Text>

                <Typography.Paragraph
                  strong
                  ellipsis
                  className="mb-0"
                >
                  {renderConversationStatus()}
                </Typography.Paragraph>
              </Space.Compact>
            </Space>
          </Col>
        </Row>
      </Col>
    </Row>
  ), [statusTag, isUnread, acceptedBy, lastMessage, searchParams.get('conversation'), livechatSettings?.livechatMode]);

  return MemoizedInboxItem;
};

InboxItem.propTypes = {
  conversationId: PropTypes.number,
  sessionId: PropTypes.string,
  endUserName: PropTypes.string,
  tenantId: PropTypes.string,
  tenantName: PropTypes.string,
  tenantImageUrl: PropTypes.string,
  lastMessage: PropTypes.object,
  startDate: PropTypes.string,
  isRead: PropTypes.bool,
  status: PropTypes.string,
  platform: PropTypes.string,
  livechatParticipants: PropTypes.array,
  transferRequests: PropTypes.array,
  isImportant: PropTypes.bool,
  onClick: PropTypes.func,
  onImportantClick: PropTypes.func
};

export default InboxItem;
